// RoomAccordion.js - collapsible Room/Category group header (rename, reorder, remove, subtotal).
import { ArrowDown, ArrowUp, ChevronDown, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import ConfirmDialog from './ConfirmDialog';

const RoomAccordion = ({
  room,
  subtotalLabel,
  onToggle,
  onRename,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  onRemove,
  canRemove,
  children,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center gap-2 bg-gray-50/80 px-3 py-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-200/60"
          title={room.collapsed ? 'Expand room' : 'Collapse room'}
        >
          <ChevronDown className={`h-4 w-4 transition-transform ${room.collapsed ? '-rotate-90' : ''}`} />
        </button>
        <input
          type="text"
          value={room.name}
          onChange={(e) => onRename(e.target.value)}
          placeholder="Room / Category name"
          className="h-8 min-w-[140px] flex-1 rounded-md border border-transparent bg-transparent px-2 text-sm font-semibold text-gray-900 hover:border-gray-200 focus:border-teal-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30"
        />
        {subtotalLabel !== undefined && subtotalLabel !== null && (
          <span className="rounded-full bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-700">
            {subtotalLabel}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={!canMoveUp}
            title="Move room up"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:border-teal-300 hover:text-teal-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ArrowUp className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={!canMoveDown}
            title="Move room down"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:border-teal-300 hover:text-teal-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ArrowDown className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setShowConfirm(true)}
            disabled={!canRemove}
            title="Remove room"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-red-500 hover:border-red-300 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {!room.collapsed && <div className="p-3">{children}</div>}

      <ConfirmDialog
        isOpen={showConfirm}
        title="Remove room?"
        message={`This will permanently remove "${room.name || 'this room'}" and all of its rows. This cannot be undone.`}
        confirmLabel="Remove"
        onCancel={() => setShowConfirm(false)}
        onConfirm={() => {
          setShowConfirm(false);
          onRemove?.();
        }}
      />
    </div>
  );
};

export default RoomAccordion;
